import React, { useCallback, useEffect, useRef, useState } from 'react';
import { oggiClinica } from '../../utils/oraItaliana';
import { Plus, Trash2, Radiation } from 'lucide-react';
import { rxAPI } from '../../services/api';
import '../../styles/RegistroRx.css';

/**
 * RegistroRx — il registro RX di un paziente.
 *
 * Prestazioni che il professionista fattura in proprio, con la quota che
 * riconosce alla struttura. NON entra in nessun conteggio della clinica:
 * non genera prestazioni eseguite, non genera spettanze, non compare in
 * fatturazione. E' un registro parallelo, e resta parallelo.
 *
 * SI COMPILA COME UN FOGLIO DI CALCOLO
 * I campi sono modificabili direttamente in riga, senza aprire una
 * finestra per ogni modifica: e' un registro che si tiene a mano, spesso
 * con due o tre righe da correggere di seguito, e una modale per ognuna
 * sarebbe sei clic per un numero.
 *
 * Si salva quando il campo perde il fuoco, non a ogni tasto: salvare a
 * ogni tasto vorrebbe dire una richiesta per carattere, e un numero
 * scritto a meta' che arriva al server.
 *
 * NESSUN CAMPO OBBLIGATORIO OLTRE ALLA DATA
 * Una riga con la sola data e' valida: chi compila spesso sa la data e
 * l'importo e scrivera' la prestazione dopo. I limiti che restano sono di
 * plausibilita' - una data impossibile, un importo negativo - e li
 * verifica il server.
 */

const euro = (cent) => (Number(cent || 0) / 100).toFixed(2).replace('.', ',');

/* Dall'euro scritto a mano ai centesimi: si accetta la virgola, che e'
   come si scrive qui, e il punto, che e' come lo scrive chi ha il tastierino. */
const numero = (testo) => {
    const s = String(testo ?? '').trim().replace(/\s/g, '').replace(',', '.');
    if (s === '') return 0;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

/* Il giorno della clinica (7/10/2026): toISOString dava quello UTC, e fra
   mezzanotte e le due proponeva ieri. */
const oggi = () => oggiClinica();

const RegistroRx = ({ pazienteId }) => {
    const [righe, setRighe] = useState([]);
    const [coordina, setCoordina] = useState(false);
    const [medici, setMedici] = useState([]);
    const [caricando, setCaricando] = useState(true);
    const [errore, setErrore] = useState(null);
    const [negato, setNegato] = useState(false);
    const [nuova, setNuova] = useState(null);
    const primoNuovo = useRef(null);

    const carica = useCallback(async () => {
        try {
            const r = await rxAPI.perPaziente(pazienteId);
            setRighe(r.data.data.righe);
            setCoordina(r.data.data.coordina);
        } catch (e) {
            if (e?.response?.status === 403) setNegato(true);
            else setErrore('Non riesco a leggere il registro');
        } finally { setCaricando(false); }
    }, [pazienteId]);

    useEffect(() => { carica(); }, [carica]);

    useEffect(() => {
        if (!coordina) return;
        rxAPI.mediciAbilitati().then((r) => setMedici(r.data.data.medici)).catch(() => {});
    }, [coordina]);

    useEffect(() => { if (nuova && primoNuovo.current) primoNuovo.current.focus(); }, [nuova]);

    /* Si salva solo se il valore e' davvero cambiato: uscire da un campo
       senza averlo toccato non deve generare una scrittura, che finirebbe
       nel registro delle modifiche come una modifica che non c'e' stata. */
    const salva = async (riga, campo, valore) => {
        const precedente = campo === 'importo' ? euro(riga.importo_cent)
                         : campo === 'costo_prodotto' ? euro(riga.costo_prodotto_cent)
                         : campo === 'data' ? String(riga.data).slice(0, 10)
                         : (riga[campo] ?? '');
        if (String(valore ?? '') === String(precedente)) return;
        setErrore(null);
        try {
            await rxAPI.modifica(riga.id, { [campo]: valore });
            carica();
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a salvare'); carica(); }
    };

    const aggiungi = async () => {
        setErrore(null);
        const d = nuova;
        if (!d.data) { setErrore('Serve la data'); return; }
        if (coordina && !d.medico_id) { setErrore('Serve il medico a cui intestare la riga'); return; }
        try {
            await rxAPI.crea(pazienteId, {
                data: d.data, prestazione: d.prestazione || null,
                importo: numero(d.importo), note: d.note || null,
                ...(coordina ? { medico_id: Number(d.medico_id),
                                 costo_prodotto: numero(d.costo_prodotto) } : {}),
            });
            setNuova(null);
            carica();
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco ad aggiungere la riga'); }
    };

    const elimina = async (riga) => {
        if (!window.confirm(`Eliminare la riga del ${new Date(riga.data).toLocaleDateString('it-IT')}`
                          + `${riga.prestazione ? ` — ${riga.prestazione}` : ''}?\n\n`
                          + 'Sparisce dal registro. Resta recuperabile da chi amministra il sistema.')) return;
        try { await rxAPI.elimina(riga.id); carica(); }
        catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a eliminare la riga'); }
    };

    if (negato) {
        return <div className="rx-negato">Il registro RX non è fra le tue schermate.</div>;
    }
    if (caricando) return <div className="rx-attesa">Carico…</div>;

    const tot = righe.reduce((a, r) => ({
        importo: a.importo + Number(r.importo_cent || 0),
        quota: a.quota + Number(r.quota_struttura_cent || 0),
        costo: a.costo + Number(r.costo_prodotto_cent || 0),
        netto: a.netto + Number(r.netto_medico_cent || 0),
    }), { importo: 0, quota: 0, costo: 0, netto: 0 });

    return (
        <div className="rx-blocco">
            <header className="rx-testa">
                <h2><Radiation size={19} aria-hidden="true" /> Registro RX</h2>
                <p>
                    Prestazioni fatturate in proprio, con la quota riconosciuta alla struttura.
                    {coordina
                        ? ' Vedi le righe di tutti i medici abilitati per questo paziente.'
                        : ' Vedi le tue righe: quelle di un collega non compaiono.'}
                    {' '}<strong>Non entra nei conteggi della clinica</strong> — non genera
                    prestazioni eseguite né spettanze, e non compare in fatturazione.
                </p>
            </header>

            {errore && <p className="rx-errore">{errore}</p>}

            <div className="rx-tabella">
                <table>
                    <thead>
                        <tr>
                            <th className="rx-data">Data</th>
                            {coordina && <th>Medico</th>}
                            <th>Prestazione</th>
                            <th className="rx-num">Importo</th>
                            <th className="rx-num">Costo prodotto</th>
                            <th className="rx-num">Base</th>
                            <th className="rx-num">Quota struttura %</th>
                            <th className="rx-num">Alla struttura</th>
                            <th className="rx-num">Netto medico</th>
                            <th>Note</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {righe.map((r) => (
                            <tr key={r.id}>
                                <td className="rx-data">
                                    <input type="date" defaultValue={String(r.data).slice(0, 10)}
                                           onBlur={(e) => salva(r, 'data', e.target.value)} />
                                </td>
                                {coordina && (
                                    <td className="rx-medico">{r.medico_cognome} {r.medico_nome}</td>
                                )}
                                <td>
                                    <input defaultValue={r.prestazione || ''} placeholder="—"
                                           onBlur={(e) => salva(r, 'prestazione', e.target.value)} />
                                </td>
                                <td className="rx-num">
                                    <input inputMode="decimal" defaultValue={euro(r.importo_cent)}
                                           onBlur={(e) => {
                                               const n = numero(e.target.value);
                                               if (n === null) { e.target.value = euro(r.importo_cent); return; }
                                               salva(r, 'importo', n);
                                           }} />
                                </td>
                                <td className="rx-num">
                                    {coordina ? (
                                        <input inputMode="decimal" defaultValue={euro(r.costo_prodotto_cent)}
                                               onBlur={(e) => {
                                                   const n = numero(e.target.value);
                                                   if (n === null) { e.target.value = euro(r.costo_prodotto_cent); return; }
                                                   salva(r, 'costo_prodotto', n);
                                               }} />
                                    ) : (
                                        <span className="rx-sola-lettura"
                                              title="Lo scrive la segreteria, che maneggia il magazzino">
                                            {euro(r.costo_prodotto_cent)} €
                                        </span>
                                    )}
                                </td>
                                {/* La base e' quello su cui si divide: importo meno il
                                    materiale. Sta in colonna perche' senza di lei la quota
                                    non torna col conto a mente - il 30% di 100 non fa 24 -
                                    e un numero che non torna a mente non viene creduto. */}
                                <td className="rx-num rx-calcolato" title="Importo meno il costo del prodotto">
                                    {euro(Number(r.importo_cent) - Number(r.costo_prodotto_cent))} €
                                </td>
                                {/* La percentuale e' quella impostata sulla scheda del
                                    medico, congelata su questa riga quando e' nata. */}
                                <td className="rx-num rx-calcolato"
                                    title={r.quota_struttura_percentuale === null
                                        ? 'Il medico non ha un’aliquota nelle sue impostazioni.'
                                        : 'Congelata su questa riga quando è stata scritta'}>
                                    {r.quota_struttura_percentuale === null ? '—' : `${r.quota_struttura_percentuale}%`}
                                </td>
                                <td className="rx-num rx-calcolato">
                                    {r.quota_struttura_percentuale === null ? '—' : `${euro(r.quota_struttura_cent)} €`}
                                </td>
                                {/* Importo meno quota meno costo dei prodotti. Lo calcola
                                    il database, non questa schermata: due posti che fanno lo
                                    stesso conto sono due posti che prima o poi non coincidono. */}
                                <td className={`rx-num rx-netto ${Number(r.netto_medico_cent) < 0 ? 'rx-negativo' : ''}`}>
                                    {euro(r.netto_medico_cent)} €
                                </td>
                                <td>
                                    <input defaultValue={r.note || ''} placeholder="—"
                                           onBlur={(e) => salva(r, 'note', e.target.value)} />
                                </td>
                                <td className="rx-fine">
                                    <button className="rx-icona" title="Elimina la riga"
                                            onClick={() => elimina(r)}>
                                        <Trash2 size={15} aria-hidden="true" />
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {nuova && (
                            <tr className="rx-nuova">
                                <td className="rx-data">
                                    <input ref={primoNuovo} type="date" value={nuova.data}
                                           onChange={(e) => setNuova({ ...nuova, data: e.target.value })} />
                                </td>
                                {coordina && (
                                    <td>
                                        <select value={nuova.medico_id}
                                                onChange={(e) => setNuova({ ...nuova, medico_id: e.target.value })}>
                                            <option value="">— medico —</option>
                                            {medici.map((m) => (
                                                <option key={m.id} value={m.id}>
                                                    {m.cognome} {m.nome}
                                                    {m.quota_struttura_rx_percentuale !== null
                                                        ? ` (${m.quota_struttura_rx_percentuale}%)` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                )}
                                <td>
                                    <input value={nuova.prestazione} placeholder="Prestazione"
                                           onChange={(e) => setNuova({ ...nuova, prestazione: e.target.value })} />
                                </td>
                                <td className="rx-num">
                                    <input inputMode="decimal" value={nuova.importo} placeholder="0,00"
                                           onChange={(e) => setNuova({ ...nuova, importo: e.target.value })} />
                                </td>
                                <td className="rx-num">
                                    {coordina ? (
                                        <input inputMode="decimal" value={nuova.costo_prodotto} placeholder="0,00"
                                               onChange={(e) => setNuova({ ...nuova, costo_prodotto: e.target.value })} />
                                    ) : <span className="rx-sola-lettura">—</span>}
                                </td>
                                <td className="rx-num rx-calcolato">al salvataggio</td>
                                <td className="rx-num rx-calcolato">dal medico</td>
                                <td className="rx-num rx-calcolato">al salvataggio</td>
                                <td className="rx-num rx-calcolato">al salvataggio</td>
                                <td>
                                    <input value={nuova.note} placeholder="Note"
                                           onChange={(e) => setNuova({ ...nuova, note: e.target.value })}
                                           onKeyDown={(e) => { if (e.key === 'Enter') aggiungi(); }} />
                                </td>
                                <td className="rx-fine">
                                    <button className="rx-si" onClick={aggiungi}>Aggiungi</button>
                                    <button className="rx-icona" onClick={() => setNuova(null)}>✕</button>
                                </td>
                            </tr>
                        )}

                        {righe.length === 0 && !nuova && (
                            <tr><td colSpan={coordina ? 11 : 10} className="rx-vuoto">
                                Nessuna riga per questo paziente.
                            </td></tr>
                        )}
                    </tbody>

                    {righe.length > 0 && (
                        <tfoot>
                            <tr>
                                <td colSpan={coordina ? 3 : 2}>{righe.length} righe</td>
                                <td className="rx-num">{euro(tot.importo)} €</td>
                                <td className="rx-num">{euro(tot.costo)} €</td>
                                <td className="rx-num">{euro(tot.importo - tot.costo)} €</td>
                                <td />
                                <td className="rx-num">{euro(tot.quota)} €</td>
                                <td className={`rx-num ${tot.netto < 0 ? 'rx-negativo' : ''}`}>
                                    {euro(tot.netto)} €
                                </td>
                                <td colSpan={2} />
                            </tr>
                            {/* Le due righe finali: cosa resta al medico e cosa va
                                alla struttura. Sono i due numeri per cui il registro
                                esiste, e stanno per esteso perche' letti da soli non
                                si confondono con i totali di colonna qui sopra. */}
                            <tr className="rx-riga-finale">
                                <td colSpan={coordina ? 9 : 8}>Netto del medico</td>
                                <td colSpan={2} className={`rx-num rx-forte ${tot.netto < 0 ? 'rx-negativo' : ''}`}>
                                    {euro(tot.netto)} €
                                </td>
                            </tr>
                            <tr className="rx-riga-finale">
                                <td colSpan={coordina ? 9 : 8}>Alla struttura</td>
                                <td colSpan={2} className="rx-num rx-forte">
                                    {euro(tot.quota)} €
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {!nuova && (
                <button className="rx-aggiungi" onClick={() => setNuova({
                    data: oggi(), medico_id: '', prestazione: '', importo: '', costo_prodotto: '', note: '',
                })}>
                    <Plus size={15} aria-hidden="true" /> Aggiungi una riga
                </button>
            )}

            <p className="rx-nota">
                Si salva uscendo dal campo. L’unico dato obbligatorio è la data: la prestazione e
                l’importo si possono scrivere dopo.
                {!coordina && ' Il costo del prodotto lo scrive la segreteria.'}
                {' '}Il costo del prodotto <strong>si toglie prima</strong> di dividere: quello che
                resta è la base, e la percentuale si applica a quella. Così il materiale lo pagano
                tutti e due nella stessa proporzione della quota. Base e netto si calcolano, non si
                scrivono.
            </p>
        </div>
    );
};

export default RegistroRx;
